import React, { useState, useEffect } from 'react';
import { PhoneCall, PlayCircle, ChevronRight, ClipboardList } from 'lucide-react';
import { FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useStrategyCalls } from '../../hooks/useStrategyCalls';
import { useStrategyCallBooking } from '../../hooks/useStrategyCallBooking';
import { useDepartmentCalls } from '../../hooks/useDepartmentCalls';
import { useTaskTime } from '../../hooks/useTaskTime';
import { getPillarScheduleLink } from '../../services/pillarsService';
import { formatTaskTimeHours } from '../../services/taskTimeService';
import { taskTimeDepartmentLabel } from '../../utils/taskTimeConstants';
import DepartmentCallsModal from './DepartmentCallsModal';
import DepartmentTaskTimeModal from './DepartmentTaskTimeModal';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

function formatCallDate(date) {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * "Implementation Hours" card for a department page: the department's strategy
 * calls plus Schedule Call and Schedule Task actions, drawing from the shared
 * hours pool.
 */
const DepartmentImplementationCard = ({ department, fallbackScheduleUrl, className = '' }) => {
  const { currentUser } = useAuth();
  const { loading: usageLoading, atLimit } = useStrategyCalls();
  const { calls, loading: callsLoading, refresh: refreshCalls } = useDepartmentCalls(department);
  const taskTime = useTaskTime();

  const [callsModalOpen, setCallsModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [scheduleLink, setScheduleLink] = useState('');
  const [userData, setUserData] = useState(null);

  const { openBooking, meetingAgendaModal } = useStrategyCallBooking({
    currentUser,
    userProfile: userData,
    disabled: atLimit,
    variant: 'department',
    requireFocus: true,
  });

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());
        const link = await getPillarScheduleLink(currentUser.uid, department);
        setScheduleLink(link);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [currentUser, department]);

  const openSchedule = () => {
    if (atLimit) return;
    const url = scheduleLink || fallbackScheduleUrl;
    if (!url) return;
    openBooking({ url, fallbackUrl: fallbackScheduleUrl });
  };

  const { allowanceHours, remainingHours, deductedHours, usedCallHours, periodEnd } = taskTime;
  const recentCalls = calls.slice(0, 3);

  const balancePct =
    allowanceHours > 0
      ? Math.min(100, Math.round((((deductedHours || 0) + (usedCallHours || 0)) / allowanceHours) * 100))
      : 0;
  const resetDisplay = periodEnd
    ? new Date(periodEnd.getTime ? periodEnd.getTime() - 1 : periodEnd).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const sectionHeader = (Icon, label, onViewAll) => (
    <div className="flex items-center justify-between mb-1.5">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon className="w-3.5 h-3.5 text-red-600" />
        {label}
      </span>
      <button
        type="button"
        onClick={onViewAll}
        className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
      >
        View all <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );

  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 flex flex-col ${className}`}>
      <div className="flex items-center justify-center gap-2 mb-1">
        <span className="text-base leading-none" aria-hidden="true">⏱️</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Implementation Hours</h2>
      </div>
      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-3">
        Calls draw from your monthly pool of hours
      </p>

      {/* Shared pool: strategy calls deduct 1h each */}
      {taskTime.loading || usageLoading ? (
        <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse mb-4" />
      ) : allowanceHours != null ? (
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600 dark:text-gray-300">
              {formatTaskTimeHours(remainingHours)} of {formatTaskTimeHours(allowanceHours)} remaining
            </span>
            {resetDisplay && <span className="text-gray-500 dark:text-gray-400">Resets {resetDisplay}</span>}
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full ${balancePct >= 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${balancePct}%` }}
            />
          </div>
        </div>
      ) : null}

      <div className="flex-1 space-y-4 mb-4">
        {/* Calls */}
        <div>
          {sectionHeader(PhoneCall, 'Calls', () => setCallsModalOpen(true))}
          {callsLoading ? (
            <div className="h-12 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ) : recentCalls.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2 text-center">
              No {taskTimeDepartmentLabel(department).toLowerCase()} calls yet.
            </p>
          ) : (
            <div className="space-y-2">
              {recentCalls.map((call) => (
                <button
                  key={call.id}
                  type="button"
                  onClick={() => setCallsModalOpen(true)}
                  className="w-full text-left flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{call.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatCallDate(call.date)} · {call.status}
                    </p>
                  </div>
                  {call.recordingUrl && <PlayCircle className="w-4 h-4 shrink-0 text-red-500" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={openSchedule}
          disabled={atLimit}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            atLimit
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
        >
          <FaCalendarAlt className="text-xs" />
          {atLimit ? 'Calls Unavailable' : 'Schedule Call'}
        </button>
        <button
          type="button"
          onClick={() => setTaskModalOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <ClipboardList className="w-4 h-4" />
          Schedule Task
        </button>
      </div>

      {callsModalOpen && (
        <DepartmentCallsModal
          isOpen={callsModalOpen}
          onClose={() => {
            setCallsModalOpen(false);
            refreshCalls();
          }}
          department={department}
          calls={calls}
          fallbackScheduleUrl={fallbackScheduleUrl}
        />
      )}
      {taskModalOpen && (
        <DepartmentTaskTimeModal
          isOpen={taskModalOpen}
          onClose={() => setTaskModalOpen(false)}
          department={department}
          taskTime={taskTime}
          initialTab="new"
        />
      )}
      {meetingAgendaModal}
    </div>
  );
};

export default DepartmentImplementationCard;
